// Authenticated fetch wrapper — auto-refreshes token on 401, logs out if refresh fails.
// Import and use instead of raw fetch() for all API calls.

import Keycloak from "keycloak-js";

const AUTH_DISABLED = import.meta.env.VITE_AUTH_DISABLED === "true";

let _keycloak: Keycloak | null = null;
let _setToken: ((token: string | undefined) => void) | null = null;
let _logout: (() => void) | null = null;
let _staticToken: string | null = null;

/** Called once from AuthProvider to wire up the Keycloak instance */
export function initAuthFetch(kc: Keycloak, setToken: (t: string | undefined) => void, logout: () => void) {
  _keycloak = kc;
  _setToken = setToken;
  _logout = logout;
}

/** Set a static token for AUTH_DISABLED mode (no refresh needed) */
export function setStaticToken(token: string) {
  _staticToken = token;
}

/**
 * Notified when any API call is refused because every membership has expired.
 *
 * The gateway can start refusing mid-session — an expiry passes while the tab is
 * open, and nothing re-fetches memberships until the next token refresh. Without
 * a central hook the user sits in the full app with every panel erroring, which
 * is the exact state AccessExpiredPage exists to replace. Keyed on the stable
 * `code`, never the prose.
 */
let _onAccessExpired: (() => void) | null = null;
export function setAccessExpiredHandler(fn: (() => void) | null) {
  _onAccessExpired = fn;
}

/**
 * Inspect a 403 for the membership-expiry code. Reads a CLONE — the caller still
 * owns the original body, which is single-use.
 */
async function flagMembershipExpiry(res: Response): Promise<Response> {
  if (res.status !== 403 || !_onAccessExpired) return res;
  try {
    const body = await res.clone().json() as { code?: string } | null;
    if (body?.code === "membership_expired") _onAccessExpired();
  } catch { /* not JSON, or already consumed — nothing to flag */ }
  return res;
}

let _impersonateOrg: string | null = null;
/**
 * While a platform_admin impersonation session is active, scope every upstream
 * request to the impersonated account via the x-impersonate-org header. The
 * gateway honors it only for a verified platform_admin caller (KAN-1820).
 */
export function setImpersonateOrg(orgId: string | null) {
  _impersonateOrg = orgId;
}

/** Get a fresh token, refreshing if needed */
async function getFreshToken(): Promise<string | null> {
  // Static token (embed mode or AUTH_DISABLED): no refresh needed
  if (_staticToken) return _staticToken;

  if (!_keycloak?.token) return null;
  try {
    // Refresh if expires in less than 60 seconds
    const refreshed = await _keycloak.updateToken(60);
    if (refreshed && _setToken) _setToken(_keycloak.token);
    return _keycloak.token ?? null;
  } catch {
    // Refresh failed — log the user out
    _logout?.();
    return null;
  }
}

/**
 * Fetch with automatic token refresh.
 * On 401: refreshes token and retries once. If refresh fails, logs out.
 *
 * @param devFallback — static token used in DEV_MODE (when Keycloak is not initialised).
 */
export async function authFetch(url: string, options?: RequestInit, devFallback?: string | null): Promise<Response> {
  // An explicit Authorization on the init wins. Callers that act as somebody
  // other than the signed-in user pass one (the widget builder's agent preview
  // runs under the selected customer's embed token); overwriting it here sent
  // their writes out as the admin while their reads used the override, which
  // showed up as the agent creating a thread in one org and then 404ing when
  // the stream looked for it in the other.
  const explicitAuth = new Headers(options?.headers).get("Authorization");
  if (explicitAuth) {
    const headers = new Headers(options?.headers);
    if (_impersonateOrg) headers.set("x-impersonate-org", _impersonateOrg);
    // No 401 re-auth: refreshing the signed-in user's Keycloak token would not
    // help a request that is deliberately not theirs.
    return fetch(url, { ...options, headers });
  }

  let token: string | null;
  if (_staticToken) {
    token = _staticToken;
  } else if (_keycloak) {
    token = await getFreshToken();
  } else {
    // DEV_MODE — Keycloak not initialised, use the static dev token from React state
    token = devFallback ?? null;
  }
  if (!token) throw new Error("Not authenticated");

  const headers = new Headers(options?.headers);
  headers.set("Authorization", `Bearer ${token}`);
  if (_impersonateOrg) headers.set("x-impersonate-org", _impersonateOrg);

  const res = await fetch(url, { ...options, headers });

  if (res.status === 401 && !AUTH_DISABLED) {
    // Token expired between refresh check — try once more
    if (!_keycloak) {
      _logout?.();
      throw new Error("Session expired");
    }
    const newToken = await getFreshToken();
    if (!newToken) {
      _logout?.();
      throw new Error("Session expired");
    }
    headers.set("Authorization", `Bearer ${newToken}`);
    return flagMembershipExpiry(await fetch(url, { ...options, headers }));
  }

  return flagMembershipExpiry(res);
}
